'use client'

import { useEffect } from 'react'

/**
 * Radix modal layers (Dialog, AlertDialog, DropdownMenu in `modal` mode) set
 * `pointer-events: none` on `<body>` while open and restore it on close. When
 * two such layers from different Radix packages overlap — e.g. a modal
 * DropdownMenu item that opens a modal AlertDialog — or when a layer is
 * force-unmounted mid-close (a node deleting itself while its dialog is open),
 * that restore can be skipped, leaving the whole page permanently unclickable.
 *
 * This guard is the app-wide safety net: whenever `pointer-events: none` is left
 * on `<body>` with no Radix overlay actually open, it clears it. It only ever
 *removes* an orphaned lock — never one a legitimately-open modal still needs.
 */

// An OPEN Radix overlay in the DOM means the body lock is intentional, so we
// must leave it alone. Every term requires `data-state="open"` — a closed or
// still-animating overlay (including a lingering `popper-content-wrapper` left
// in the DOM mid-exit) must NOT count, or the guard would refuse to clear a
// genuinely orphaned lock. Dialog/AlertDialog are centered (not popper-
// positioned); menus/listboxes/popovers render inside a popper wrapper, so an
// open descendant there covers them.
const OPEN_OVERLAY_SELECTOR =
  '[data-state="open"][role="dialog"],' +
  '[data-state="open"][role="alertdialog"],' +
  '[data-state="open"][role="menu"],' +
  '[data-state="open"][role="listbox"],' +
  '[data-radix-popper-content-wrapper] [data-state="open"]'

export function PointerEventsGuard() {
  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }
    const body = document.body
    let frame = 0

    const check = () => {
      frame = 0
      if (body.style.pointerEvents !== 'none') {
        return
      }
      // A modal is genuinely open — the lock is intentional.
      if (document.querySelector(OPEN_OVERLAY_SELECTOR)) {
        return
      }
      body.style.pointerEvents = ''
    }

    const schedule = () => {
      // Coalesce bursts of mutations into one check on the next frame, after
      // Radix has finished its own commit (body style + overlay data-state).
      if (frame) {
        return
      }
      frame = requestAnimationFrame(check)
    }

    // Two observations are needed, and they must be separate observers:
    // calling observe() twice on the same target REPLACES the prior options.
    //
    // 1) `<body>`'s own inline style — catches the lock being written/cleared
    //    (kept narrow, no subtree, so descendant style writes don't churn it).
    const styleObserver = new MutationObserver(schedule)
    styleObserver.observe(body, {
      attributes: true,
      attributeFilter: ['style'],
    })

    // 2) Node add/remove anywhere under `<body>` — catches an overlay (and its
    //    portal) being removed *without* a following body-style write, which is
    //    the stuck-lock failure mode and would otherwise never re-trigger a
    //    style-only observer. The check is O(1) and early-returns unless the
    //    body is actually locked, so this broad subtree watch stays cheap.
    const treeObserver = new MutationObserver(schedule)
    treeObserver.observe(body, { childList: true, subtree: true })

    // Run once in case a lock was already stranded before we attached.
    schedule()

    return () => {
      styleObserver.disconnect()
      treeObserver.disconnect()
      if (frame) {
        cancelAnimationFrame(frame)
      }
    }
  }, [])

  return null
}
